import React, { useState } from "react";
import { useProductManagement } from "../../components/CompanyAdmin/ProductManagementWrapper";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../../stores/auth-store";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import Modal from "../../components/UI/Modal";
import TextInput from "../../components/UI/TextInput";
import { Button } from "../../components/UI/Button";
import Switch from "../../components/UI/Switch";
import { useForm } from "react-hook-form";
import { CategoryProductSortManager } from "../../components/CompanyAdmin/ProductManagement/CategoryProductSortManager";
import { DuplicateProductModal } from "../../components/CompanyAdmin/ProductManagement/DuplicateProductModal";
import type {
  CategoryDto,
  ProductLightDto,
  ProductUsage,
  DuplicateProductRequest,
} from "../../../domain/types/company/company-product-system";
import {
  PRODUCT_TYPE_LABELS,
  PAPER_TYPE_LABELS,
} from "../../../domain/types/company/company-product-system";
import type {
  ProductType,
  PaperType,
} from "../../../domain/types/company/company-product-system";

const PRODUCT_TYPES: ProductType[] = [
  "POSTER",
  "BUSINESS_CARD",
  "LEAFLET",
  "STICKER",
  "HANGING_BANNER",
  "STAND_BANNER",
  "SOFT_COVER_BOOK",
  "HARD_COVER_BOOK",
  "OTHER",
];

const PAPER_TYPE_INFO: Record<
  PaperType,
  { description: string; tags: string[] }
> = {
  ROLL_PAPER: {
    description: "종이의 1m\u00B2(회배) 당 금액으로 상품의 금액이 결정됩니다",
    tags: ["플로터 출력물", "배너", "현수막"],
  },
  SHEET_PAPER: {
    description:
      "입력한 크기를 출력할 수 있는 최소 종이 규격을 찾아 금액이 결정됩니다",
    tags: ["프린터 출력물", "포스터", "명함"],
  },
};

// ProductType별 허용되는 PaperType 매핑
const ALLOWED_PAPER_TYPES: Record<ProductType, PaperType[]> = {
  POSTER: ["SHEET_PAPER"],
  BUSINESS_CARD: ["SHEET_PAPER"],
  LEAFLET: ["SHEET_PAPER"],
  STICKER: ["SHEET_PAPER"],
  HANGING_BANNER: ["ROLL_PAPER"],
  STAND_BANNER: ["ROLL_PAPER"],
  SOFT_COVER_BOOK: ["SHEET_PAPER"],
  HARD_COVER_BOOK: ["SHEET_PAPER"],
  OTHER: ["ROLL_PAPER", "SHEET_PAPER"], // OTHER는 둘 다 허용
};

export const ProductManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const {
    categories,
    loading,
    error,
    createCategory,
    updateCategory,
    toggleCategoryEnable,
    deleteCategory,
    createProduct,
    renameProduct,
    duplicateProduct,
    deleteProduct,
  } = useProductManagement();

  // ── 카테고리 추가 ──
  interface NewCategoryForm {
    name: string;
  }
  const newCategoryForm = useForm<NewCategoryForm>({ shouldUnregister: false });
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);

  const handleNewCategorySubmit = async (data: NewCategoryForm) => {
    if (!data.name.trim()) return;
    try {
      await createCategory(data.name);
      newCategoryForm.reset();
      setShowAddCategoryModal(false);
    } catch {
      alert("카테고리 생성에 실패했습니다.");
    }
  };

  // ── 카테고리 수정 ──
  interface EditCategoryForm {
    name: string;
  }
  const editCategoryForm = useForm<EditCategoryForm>({
    shouldUnregister: false,
  });
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(
    null,
  );

  const openEditCategoryModal = (category: CategoryDto) => {
    setEditingCategoryId(category.id);
    editCategoryForm.setValue("name", category.name);
    setShowEditCategoryModal(true);
  };

  const handleEditCategorySubmit = async (data: EditCategoryForm) => {
    if (!data.name.trim() || !editingCategoryId) return;
    try {
      await updateCategory(editingCategoryId, data.name);
      editCategoryForm.reset();
      setShowEditCategoryModal(false);
      setEditingCategoryId(null);
    } catch {
      alert("카테고리 수정에 실패했습니다.");
    }
  };

  // ── 카테고리 활성화/비활성화 ──
  const handleToggleCategoryEnable = async (category: CategoryDto) => {
    const nextIsEnable = !category.isEnable;
    const action = nextIsEnable ? "활성화" : "비활성화";
    const warning = nextIsEnable
      ? `"${category.name}" 카테고리를 활성화하시겠습니까?\n쇼핑몰에 다시 노출됩니다.`
      : `"${category.name}" 카테고리를 비활성화하시겠습니까?\n쇼핑몰에서 카테고리와 하위 상품이 숨겨집니다.`;
    if (!window.confirm(warning)) return;
    try {
      await toggleCategoryEnable(category.id, nextIsEnable);
    } catch {
      alert(`카테고리 ${action}에 실패했습니다.`);
    }
  };

  // ── 카테고리 삭제 ──
  const handleDeleteCategory = async (category: CategoryDto) => {
    const msg =
      category.products.length > 0
        ? `"${category.name}" 카테고리에 ${category.products.length}개의 상품이 있습니다. 정말 삭제하시겠습니까?`
        : `"${category.name}" 카테고리를 삭제하시겠습니까?`;
    if (!window.confirm(msg)) return;
    try {
      await deleteCategory(category.id);
    } catch {
      alert("카테고리 삭제에 실패했습니다.");
    }
  };

  // ── 상품 삭제 ──
  // 사용 중이 아니면 기존처럼 confirm 한 번으로 끝난다.
  // 주문/장바구니/견적 문의/이벤트 할인에서 아직 쓰고 있으면 서버가 삭제를 보류하고
  // 사용처를 알려주는데, 그때만 경고 모달을 띄우고 confirmed=true 로 다시 요청한다.
  const [productInUse, setProductInUse] = useState<{
    product: ProductLightDto;
    usages: ProductUsage[];
  } | null>(null);
  const [deletingInUse, setDeletingInUse] = useState(false);

  const handleDeleteProduct = async (
    e: React.MouseEvent,
    product: ProductLightDto,
  ) => {
    e.stopPropagation();
    if (!window.confirm(`"${product.name}" 상품을 삭제하시겠습니까?`)) return;
    try {
      const result = await deleteProduct(product.id);
      if (!result.deleted && result.usages.length > 0) {
        setProductInUse({ product, usages: result.usages });
      }
    } catch {
      alert("상품 삭제에 실패했습니다.");
    }
  };

  const handleConfirmDeleteProductInUse = async () => {
    if (!productInUse || deletingInUse) return;
    setDeletingInUse(true);
    try {
      await deleteProduct(productInUse.product.id, true);
      setProductInUse(null);
    } catch {
      alert("상품 삭제에 실패했습니다.");
    } finally {
      setDeletingInUse(false);
    }
  };

  // ── 상품 이름 변경 ──
  interface RenameProductForm {
    name: string;
  }
  const renameProductForm = useForm<RenameProductForm>({
    shouldUnregister: false,
  });
  const [showRenameProductModal, setShowRenameProductModal] = useState(false);
  const [renamingProduct, setRenamingProduct] = useState<ProductLightDto | null>(
    null,
  );

  const openRenameProductModal = (product: ProductLightDto) => {
    setRenamingProduct(product);
    renameProductForm.setValue("name", product.name);
    setShowRenameProductModal(true);
  };

  const handleRenameProductSubmit = async (data: RenameProductForm) => {
    if (!data.name.trim() || !renamingProduct) return;
    try {
      await renameProduct(renamingProduct.id, data.name.trim());
      renameProductForm.reset();
      setShowRenameProductModal(false);
      setRenamingProduct(null);
    } catch {
      alert("상품 이름 변경에 실패했습니다.");
    }
  };

  // ── 상품 복제 ──
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatingProduct, setDuplicatingProduct] = useState<{
    product: ProductLightDto;
    categoryId: number;
  } | null>(null);

  const openDuplicateModal = (product: ProductLightDto, categoryId: number) => {
    setDuplicatingProduct({ product, categoryId });
    setShowDuplicateModal(true);
  };

  const handleDuplicateProduct = async (request: DuplicateProductRequest) => {
    if (!duplicatingProduct) return;
    await duplicateProduct(duplicatingProduct.product.id, request);
    setShowDuplicateModal(false);
    setDuplicatingProduct(null);
  };

  // ── 상품 추가 다이얼로그 (3단계) ──
  const [addProductStep, setAddProductStep] = useState<number | null>(null);
  const [addProductCategoryId, setAddProductCategoryId] = useState<
    number | null
  >(null);
  const [selectedProductType, setSelectedProductType] =
    useState<ProductType | null>(null);
  const [selectedPaperType, setSelectedPaperType] = useState<PaperType | null>(
    null,
  );
  const [productName, setProductName] = useState("");
  const [creatingProduct, setCreatingProduct] = useState(false);

  const openAddProductFlow = (categoryId: number) => {
    setAddProductCategoryId(categoryId);
    setSelectedProductType(null);
    setSelectedPaperType(null);
    setProductName("");
    setAddProductStep(1);
  };

  const handleStep1Confirm = () => {
    if (!selectedProductType) return;

    // 허용되는 PaperType 확인
    const allowedPaperTypes = ALLOWED_PAPER_TYPES[selectedProductType];

    if (allowedPaperTypes.length === 1) {
      // 허용되는 PaperType이 하나뿐이면 자동 선택 후 Step 3로 이동
      setSelectedPaperType(allowedPaperTypes[0]);
      setAddProductStep(3);
    } else {
      // 여러 개면 Step 2로 이동하여 선택하도록
      setAddProductStep(2);
    }
  };

  const handleStep2Confirm = () => {
    if (!selectedPaperType) return;
    setAddProductStep(3);
  };

  const handleStep3Confirm = async () => {
    if (
      !addProductCategoryId ||
      !selectedProductType ||
      !selectedPaperType ||
      !productName.trim()
    )
      return;
    try {
      setCreatingProduct(true);
      const newProductId = await createProduct({
        categoryId: addProductCategoryId,
        name: productName.trim(),
        productType: selectedProductType,
        paperType: selectedPaperType,
      });
      setAddProductStep(null);
      if (newProductId) {
        navigate(`/company/${user?.companyId}/products/${newProductId}`);
      }
    } catch {
      alert("상품 생성에 실패했습니다.");
    } finally {
      setCreatingProduct(false);
    }
  };

  // ── 케밥 메뉴 ──
  const [openKebabMenuId, setOpenKebabMenuId] = useState<number | null>(null);
  const [openProductKebabMenuId, setOpenProductKebabMenuId] = useState<
    number | null
  >(null);

  return (
    <>
      {/* ── Step 1: 상품 유형 선택 ── */}
      {addProductStep === 1 && (
        <Modal
          setIsModalVisible={() => setAddProductStep(null)}
          title="추가할 상품을 선택해주세요"
        >
          <div className="w-[70vw] max-w-[31.25rem]" />
          <div className="grid grid-cols-3 gap-3 mb-6">
            {PRODUCT_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setSelectedProductType(type)}
                className={`py-3.5 px-3 rounded-xl text-sm font-medium transition-all ${
                  selectedProductType === type
                    ? "bg-[#4a6cf7]/10 text-[#4a6cf7] ring-2 ring-[#4a6cf7]"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {PRODUCT_TYPE_LABELS[type]}
              </button>
            ))}
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleStep1Confirm}
              disabled={!selectedProductType}
              className="px-6 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
            >
              확인
            </button>
          </div>
        </Modal>
      )}

      {/* ── Step 2: 금액 결정 방식 선택 ── */}
      {addProductStep === 2 && (
        <Modal
          setIsModalVisible={() => setAddProductStep(null)}
          title="상품의 금액 결정 방식을 선택해주세요"
        >
          <div className="w-[70vw] max-w-[37.5rem]" />
          <div className="grid grid-cols-2 gap-4 mb-6">
            {(["ROLL_PAPER", "SHEET_PAPER"] as PaperType[])
              .filter((type) =>
                selectedProductType
                  ? ALLOWED_PAPER_TYPES[selectedProductType].includes(type)
                  : true
              )
              .map((type) => {
              const info = PAPER_TYPE_INFO[type];
              const selected = selectedPaperType === type;
              const imgSrc =
                type === "ROLL_PAPER"
                  ? "/assets/images/plotter.png"
                  : "/assets/images/printer.png";
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setSelectedPaperType(type)}
                  className={`flex flex-col items-center text-center rounded-xl border-2 transition-all overflow-hidden ${
                    selected
                      ? "border-[#4a6cf7] bg-[#4a6cf7]/[0.03]"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {/* 이미지 영역 */}
                  <div className="w-full flex items-center justify-center pt-5 pb-3 px-6">
                    <img
                      src={imgSrc}
                      alt={PAPER_TYPE_LABELS[type]}
                      className="w-[120px] h-[120px] object-contain"
                    />
                  </div>

                  {/* 텍스트 영역 */}
                  <div className="px-4 pb-5">
                    <p
                      className={`text-base font-bold mb-1.5 ${selected ? "text-[#4a6cf7]" : "text-gray-900"}`}
                    >
                      {PAPER_TYPE_LABELS[type]}
                    </p>
                    <p
                      className={`text-xs leading-relaxed mb-4 ${selected ? "text-[#4a6cf7]/70" : "text-gray-500"}`}
                    >
                      {info.description}
                    </p>
                    <p className="text-xs text-gray-400 mb-2">
                      이런 상품에서 사용해보세요
                    </p>
                    <div className="flex flex-wrap justify-center gap-1.5">
                      {info.tags.map((tag) => (
                        <span
                          key={tag}
                          className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                            selected
                              ? "bg-[#4a6cf7]/10 text-[#4a6cf7]"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleStep2Confirm}
              disabled={!selectedPaperType}
              className="px-6 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
            >
              확인
            </button>
          </div>
        </Modal>
      )}

      {/* ── Step 3: 상품 이름 입력 ── */}
      {addProductStep === 3 && (
        <Modal
          setIsModalVisible={() => setAddProductStep(null)}
          title="상품 이름을 입력해주세요"
        >
          <div className="w-[70vw] max-w-[28rem]" />
          <div className="mb-5">
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && productName.trim() && !creatingProduct)
                  handleStep3Confirm();
              }}
              maxLength={100}
              placeholder="예: A4 포스터, 고급 명함"
              autoFocus
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:border-[#4a6cf7] focus:outline-none"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleStep3Confirm}
              disabled={!productName.trim() || creatingProduct}
              className="px-5 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-800 transition-colors"
            >
              {creatingProduct ? "생성 중..." : "확인"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── 카테고리 추가 모달 ── */}
      {showAddCategoryModal && (
        <Modal
          setIsModalVisible={setShowAddCategoryModal}
          title="새 카테고리 추가"
        >
          <div className="w-[70vw] max-w-[31.25rem]" />
          <form
            className="flex-col overflow-hidden text-[14px]"
            onSubmit={newCategoryForm.handleSubmit(handleNewCategorySubmit)}
          >
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                카테고리 이름
              </label>
              <TextInput
                error={{
                  message: newCategoryForm.formState.errors.name?.message ?? "",
                  isMessageVisible: true,
                  isIconVisible: true,
                }}
                {...newCategoryForm.register("name", {
                  required: { value: true, message: "필수 항목이에요" },
                })}
                maxLength={50}
                placeholder="예: 포스터, 명함"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-[0.75rem]">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddCategoryModal(false);
                  newCategoryForm.reset();
                }}
              >
                취소
              </Button>
              <Button variant="primary" type="submit">
                추가
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── 카테고리 수정 모달 ── */}
      {showEditCategoryModal && (
        <Modal
          setIsModalVisible={setShowEditCategoryModal}
          title="카테고리 수정"
        >
          <div className="w-[70vw] max-w-[31.25rem]" />
          <form
            className="flex-col overflow-hidden text-[14px]"
            onSubmit={editCategoryForm.handleSubmit(handleEditCategorySubmit)}
          >
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                카테고리 이름
              </label>
              <TextInput
                error={{
                  message:
                    editCategoryForm.formState.errors.name?.message ?? "",
                  isMessageVisible: true,
                  isIconVisible: true,
                }}
                {...editCategoryForm.register("name", {
                  required: { value: true, message: "필수 항목이에요" },
                })}
                maxLength={50}
                placeholder="카테고리 이름 입력"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-[0.75rem]">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowEditCategoryModal(false);
                  editCategoryForm.reset();
                }}
              >
                취소
              </Button>
              <Button variant="primary" type="submit">
                수정
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── 상품 이름 변경 모달 ── */}
      {showRenameProductModal && renamingProduct && (
        <Modal
          setIsModalVisible={setShowRenameProductModal}
          title="상품 이름 변경"
        >
          <div className="w-[70vw] max-w-[28rem]" />
          <form
            className="flex-col overflow-hidden text-[14px]"
            onSubmit={renameProductForm.handleSubmit(handleRenameProductSubmit)}
          >
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                상품 이름
              </label>
              <TextInput
                error={{
                  message: renameProductForm.formState.errors.name?.message ?? "",
                  isMessageVisible: true,
                  isIconVisible: true,
                }}
                {...renameProductForm.register("name", {
                  required: { value: true, message: "필수 항목이에요" },
                })}
                maxLength={100}
                placeholder="상품 이름 입력"
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-[0.75rem]">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRenameProductModal(false);
                  renameProductForm.reset();
                }}
              >
                취소
              </Button>
              <Button variant="primary" type="submit">
                변경
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── 사용 중인 상품 삭제 재확인 모달 ── */}
      {productInUse && (
        <Modal
          setIsModalVisible={() => setProductInUse(null)}
          title="사용 중인 상품 삭제"
        >
          <div className="w-[70vw] max-w-[28rem]" />
          <div className="flex-col overflow-hidden text-[14px]">
            <p className="text-[#555555] mb-3">
              <span className="font-medium text-[#222222]">
                &quot;{productInUse.product.name}&quot;
              </span>{" "}
              상품을 사용 중인 곳이 있습니다.
            </p>
            <ul className="mb-3 rounded-[8px] border border-[#F24A4A]/20 bg-[#FEF2F2] px-4 py-3">
              {productInUse.usages.map((usage) => (
                <li
                  key={usage.type}
                  className="flex justify-between py-[2px] text-[13px] text-[#F24A4A]"
                >
                  <span>{usage.label}</span>
                  <span className="font-medium">{usage.count}건</span>
                </li>
              ))}
            </ul>
            <p className="mb-4 text-[13px] text-[#888888]">
              삭제하면 쇼핑몰에서 상품이 내려가고, 위 항목에서는 상품 정보를 더 이상
              볼 수 없습니다. 그래도 삭제하시겠습니까?
            </p>
            <div className="flex justify-end gap-[0.75rem]">
              <Button
                type="button"
                variant="outline"
                onClick={() => setProductInUse(null)}
                disabled={deletingInUse}
              >
                취소
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={handleConfirmDeleteProductInUse}
                loading={deletingInUse}
              >
                삭제
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── 상품 복제 모달 ── */}
      {showDuplicateModal && duplicatingProduct && (
        <DuplicateProductModal
          sourceProduct={duplicatingProduct.product}
          sourceCategoryId={duplicatingProduct.categoryId}
          categories={categories}
          onClose={() => {
            setShowDuplicateModal(false);
            setDuplicatingProduct(null);
          }}
          onSubmit={handleDuplicateProduct}
        />
      )}

      {/* ── 페이지 본문 ── */}
      <CompanyAdminLayout>
        <div className="p-6 bg-gray-50 min-h-screen">
          {/* 로딩 */}
          {loading && (
            <div className="flex items-center justify-center py-32">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-gray-300 border-t-[#4a6cf7] rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-gray-500">
                  상품 목록을 불러오고 있습니다
                </p>
              </div>
            </div>
          )}

          {/* 에러 */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* 빈 상태 */}
          {!loading && !error && categories.length === 0 && (
            <div className="flex flex-col items-center justify-center py-32">
              <div className="w-16 h-16 bg-gray-200 rounded-2xl flex items-center justify-center mb-5">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
              <p className="text-gray-500 text-sm mb-1">
                아직 생성된 카테고리가 없습니다
              </p>
              <p className="text-gray-400 text-sm mb-5">
                카테고리를 만들고 상품을 추가해보세요
              </p>
              <button
                onClick={() => setShowAddCategoryModal(true)}
                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
              >
                첫 번째 카테고리 만들기
              </button>
            </div>
          )}

          {/* 카테고리 목록 */}
          {!loading && !error && categories.length > 0 && (
            <div className="space-y-6">
              {/* 순서 관리 UI */}
              <CategoryProductSortManager />
              {categories.map((category) => (
                <div
                  key={category.id}
                  className={`bg-white rounded-xl border border-gray-200 overflow-hidden ${
                    category.isEnable === false ? "opacity-50" : ""
                  }`}
                >
                  {/* 카테고리 헤더 */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                      <h3 className="text-base font-bold text-gray-900">
                        {category.name}
                      </h3>
                      <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-0.5 rounded-full">
                        {category.products.length}개
                      </span>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* 활성화 스위치 */}
                      <div
                        onClick={() => handleToggleCategoryEnable(category)}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <span
                          className={`text-xs font-medium ${
                            category.isEnable !== false
                              ? "text-[#4285F4]"
                              : "text-[#888]"
                          }`}
                        >
                          {category.isEnable !== false ? "활성화" : "비활성화"}
                        </span>
                        <Switch isEnabled={category.isEnable !== false} />
                      </div>

                      {/* 케밥 메뉴 */}
                      <div className="relative">
                      <button
                        onClick={() =>
                          setOpenKebabMenuId(
                            openKebabMenuId === category.id
                              ? null
                              : category.id,
                          )
                        }
                        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                        </svg>
                      </button>

                      {openKebabMenuId === category.id && (
                        <>
                          <div
                            className="fixed inset-0 z-10"
                            onClick={() => setOpenKebabMenuId(null)}
                          />
                          <div className="absolute right-0 mt-1 w-28 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                            <button
                              onClick={() => {
                                openEditCategoryModal(category);
                                setOpenKebabMenuId(null);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-600"
                            >
                              이름 변경
                            </button>
                            <button
                              onClick={() => {
                                handleDeleteCategory(category);
                                setOpenKebabMenuId(null);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-red-500"
                            >
                              삭제
                            </button>
                          </div>
                        </>
                      )}
                      </div>
                    </div>
                  </div>

                  {/* 상품 카드 그리드 */}
                  <div className="p-5">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                      {category.products.map((product) => (
                        <div
                          key={product.id}
                          className="bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer group/card hover:shadow-md hover:border-gray-300 transition-all"
                          onClick={() =>
                            navigate(
                              `/company/${user?.companyId}/products/${product.id}`,
                            )
                          }
                        >
                          {/* 썸네일 */}
                          <div className="relative aspect-[5/4] bg-gray-100">
                            {product.thumbnail && (
                              <img
                                src={product.thumbnail}
                                alt={product.name}
                                className="w-full h-full object-cover"
                              />
                            )}
                            {product.isEnable === false && (
                              <span className="absolute top-2 left-2 text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#f5f5f5] text-[#888]">
                                판매중지
                              </span>
                            )}
                            <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
                              <div
                                className="relative"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  onClick={() =>
                                    setOpenProductKebabMenuId(
                                      openProductKebabMenuId === product.id
                                        ? null
                                        : product.id,
                                    )
                                  }
                                  className="w-6 h-6 bg-white/90 hover:bg-gray-100 rounded-full flex items-center justify-center shadow-sm"
                                >
                                  <svg
                                    className="w-3.5 h-3.5 text-gray-400"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                                  </svg>
                                </button>

                                {openProductKebabMenuId === product.id && (
                                  <>
                                    <div
                                      className="fixed inset-0 z-10"
                                      onClick={() =>
                                        setOpenProductKebabMenuId(null)
                                      }
                                    />
                                    <div className="absolute right-0 mt-1 w-28 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                                      <button
                                        onClick={() => {
                                          openRenameProductModal(product);
                                          setOpenProductKebabMenuId(null);
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-600"
                                      >
                                        이름 변경
                                      </button>
                                      <button
                                        onClick={() => {
                                          openDuplicateModal(
                                            product,
                                            category.id,
                                          );
                                          setOpenProductKebabMenuId(null);
                                        }}
                                        className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm text-gray-600"
                                      >
                                        복제하기
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>

                              <button
                                onClick={(e) => handleDeleteProduct(e, product)}
                                className="w-6 h-6 bg-white/90 hover:bg-red-50 rounded-full flex items-center justify-center shadow-sm"
                              >
                                <svg
                                  className="w-3 h-3 text-gray-400 hover:text-red-500"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>

                          {/* 상품 정보 */}
                          <div className="px-3 py-3">
                            <div className="flex justify-between items-baseline gap-2 mb-1">
                              <span className="font-semibold text-sm text-gray-900 truncate">
                                {product.name}
                              </span>
                              <span className="text-sm font-semibold text-gray-900 shrink-0">
                                {"\u20A9"}
                                {product.price.toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs text-gray-400 line-clamp-2">
                              {product.description || "설명 없음"}
                            </p>
                          </div>
                        </div>
                      ))}

                      {/* 상품 추가하기 카드 */}
                      <div
                        className="bg-gray-100 rounded-xl cursor-pointer group/add hover:bg-gray-200 transition-all flex flex-col items-center justify-center min-h-[160px]"
                        onClick={() => openAddProductFlow(category.id)}
                      >
                        <div className="w-10 h-10 rounded-full bg-gray-200 group-hover/add:bg-gray-300 flex items-center justify-center mb-2.5 transition-colors">
                          <svg
                            className="w-5 h-5 text-gray-400 group-hover/add:text-gray-600 transition-colors"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-500 group-hover/add:text-gray-700 transition-colors">
                          상품 추가
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* 카테고리 추가 버튼 */}
              <button
                onClick={() => setShowAddCategoryModal(true)}
                className="w-full bg-gray-100 hover:bg-gray-200 rounded-xl py-4 flex items-center justify-center gap-2 text-sm text-gray-500 font-medium hover:text-gray-700 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                카테고리 추가하기
              </button>
            </div>
          )}
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default ProductManagementPage;